import React, { useState, useEffect } from 'react';
import { usePlayers } from '../contexts/PlayerContext';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Layout } from '../components/layout/Layout';
import { apiClient } from '../api/client';
import { GeneratedTeams, TeamSize, GenerationModel } from '../types';
import { getBalanceQuality } from '../utils/calculations';
import { TeamRevealModal } from '../components/generate/TeamRevealModal';
import { V5ResultDisplay } from '../components/generate/V5ResultDisplay';
import { DraftResult } from '../types';
import { V6ResultDisplay } from '../components/generate/V6ResultDisplay';

type ModelTab = 'v1' | 'v2' | 'v3' | 'super' | 'v4-beta' | 'super-beta' | 'v5' | 'draft' | 'v6';

export function GenerateTeams() {
  const { players, selectedPlayerIds, togglePlayerSelection, selectAllPlayers, deselectAllPlayers } = usePlayers();
  const [activeModel, setActiveModel] = useState<ModelTab>('v1');
  const [teamSize, setTeamSize] = useState<TeamSize>(7);
  const [dedicatedGKTeamA, setDedicatedGKTeamA] = useState<string | null>(null); // Team A dedicated GK
  const [dedicatedGKTeamB, setDedicatedGKTeamB] = useState<string | null>(null); // Team B dedicated GK
  const [generating, setGenerating] = useState(false);
  const [generatedTeamsByModel, setGeneratedTeamsByModel] = useState<Record<ModelTab, GeneratedTeams | null>>({
    'v1': null,
    'v2': null,
    'v3': null,
    'super': null,
    'v4-beta': null,
    'super-beta': null,
    'v5': null,
    'draft': null,
    'v6': null,
  });
  const [v5Result, setV5Result] = useState<any | null>(null);
  const [draftResult, setDraftResult] = useState<DraftResult | null>(null);
  const [v6Result, setV6Result] = useState<any | null>(null);
  const [matchDate, setMatchDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [bibsTeam, setBibsTeam] = useState<'A' | 'B'>('B');
  const [betaModeEnabled, setBetaModeEnabled] = useState(false);
  const [loadingBeta, setLoadingBeta] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [algorithmVisibility, setAlgorithmVisibility] = useState({
    show_algorithm_v1: true,
    show_algorithm_v2: true,
    show_algorithm_v3: true,
    show_algorithm_v4: false,
    show_algorithm_v5: true,
    show_algorithm_v6: true,
    show_algorithm_super: true,
    show_algorithm_super_beta: false,
    show_algorithm_draft: true,
  });

  // Get the generated teams for the currently active model
  const generatedTeams = generatedTeamsByModel[activeModel];

  // Sort players alphabetically by name
  const sortedPlayers = [...players].sort((a, b) => a.name.localeCompare(b.name));

  const selectedPlayers = sortedPlayers.filter((p) => selectedPlayerIds.has(p.id));
  const requiredPlayers = teamSize * 2;
  const canGenerate = selectedPlayers.length === requiredPlayers &&
    (activeModel === 'v6' || selectedPlayers.every((p) => p.calculated !== null));

  // Load BETA mode status and algorithm visibility
  useEffect(() => {
    const loadBetaMode = async () => {
      try {
        const response = await apiClient.getBetaModeEnabled();
        setBetaModeEnabled(response.beta_mode_enabled);
      } catch (err) {
        console.error('Failed to load BETA mode status:', err);
      } finally {
        setLoadingBeta(false);
      }
    };

    const loadAlgorithmVisibility = async () => {
      try {
        const response = await apiClient.getAlgorithmVisibility();
        setAlgorithmVisibility(response.data);
      } catch (err) {
        console.error('Failed to load algorithm visibility:', err);
      }
    };

    loadBetaMode();
    loadAlgorithmVisibility();
  }, []);

  const handleGenerate = async (regenerate: boolean = false) => {
    try {
      setError('');
      setGenerating(true);

      const requestData = {
        player_ids: Array.from(selectedPlayerIds),
        team_size: teamSize,
        // Include current teams when regenerating to ensure diversity
        current_teams: regenerate && generatedTeams ? {
          team_a: generatedTeams.team_a,
          team_b: generatedTeams.team_b,
        } : undefined,
        dedicated_gk_team_a: dedicatedGKTeamA,
        dedicated_gk_team_b: dedicatedGKTeamB,
      };

      let response;
      if (activeModel === 'v2') {
        response = await apiClient.generateTeamsV2(requestData);
      } else if (activeModel === 'v3') {
        response = await apiClient.generateTeamsV3(requestData);
      } else if (activeModel === 'super') {
        response = await apiClient.generateTeamsSuper(requestData);
      } else if (activeModel === 'v4-beta') {
        response = await apiClient.generateTeamsV4Beta(requestData);
      } else if (activeModel === 'super-beta') {
        response = await apiClient.generateTeamsSuperBeta(requestData);
      } else if (activeModel === 'v5') {
        const v5Response = await apiClient.generateTeamsV5(requestData);
        setV5Result(v5Response);
        return; // V5 uses separate state
      } else if (activeModel === 'draft') {
        const draftResponse = await apiClient.generateTeamsDraft(requestData);
        setDraftResult(draftResponse);
        return; // Draft uses separate state
      } else if (activeModel === 'v6') {
        console.log('[V6 Frontend] Starting V6 generation...');
        const v6Response = await apiClient.generateTeamsV6(requestData);
        console.log('[V6 Frontend] Got response:', v6Response);
        setV6Result(v6Response);
        return; // V6 uses own response format
      } else {
        response = await apiClient.generateTeams(requestData);
      }

      // Store the generated teams for this specific model
      setGeneratedTeamsByModel(prev => ({
        ...prev,
        [activeModel]: response,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate teams');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveMatch = async () => {
    // Support V6 result format
    if (activeModel === 'v6' && v6Result) {
      try {
        setError('');
        await apiClient.saveMatch({
          date: new Date(matchDate).toISOString(),
          team_size: teamSize,
          generation_method: 'AUTO',
          generation_model: activeModel,
          teams: {
            team_a: v6Result.team_a,
            team_b: v6Result.team_b,
          },
          dedicated_gk_team_a: v6Result.dedicated_gk_team_a,
          dedicated_gk_team_b: v6Result.dedicated_gk_team_b,
          statistics: v6Result.statistics,
          balance: v6Result.balance,
        });

        alert('Match saved to history!');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save match');
      }
      return;
    }

    if (!generatedTeams) return;

    try {
      setError('');
      await apiClient.saveMatch({
        date: new Date(matchDate).toISOString(),
        team_size: teamSize,
        generation_method: 'AUTO',
        generation_model: activeModel,
        teams: {
            team_a: generatedTeams.team_a,
            team_b: generatedTeams.team_b,
          },
          dedicated_gk_team_a: generatedTeams.dedicated_gk_team_a,
          dedicated_gk_team_b: generatedTeams.dedicated_gk_team_b,
        statistics: generatedTeams.statistics,
        balance: generatedTeams.balance,
      });

      alert('Match saved to history!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save match');
    }
  };

  const handleCopyTeams = async () => {
    // Support V6 result format
    const teams = activeModel === 'v6' && v6Result ? v6Result : generatedTeams;
    if (!teams) return;

    const teamAName = bibsTeam === 'A' ? 'TEAM A (bibs)' : 'TEAM A';
    const teamBName = bibsTeam === 'B' ? 'TEAM B (bibs)' : 'TEAM B';

    const text = `⚽ TEAM SELECTIONS

🔵 ${teamAName}:
${teams.team_a.map((p) => `• ${p.name}`).join('\n')}

🔴 ${teamBName}:
${teams.team_b.map((p) => `• ${p.name}`).join('\n')}`;

    try {
      await navigator.clipboard.writeText(text);
      alert('Teams copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers or insecure contexts
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        alert('Teams copied to clipboard!');
      } catch (e) {
        alert('Failed to copy teams. Please copy manually.');
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textarea);
    }
  };

  const handleShareTeams = async () => {
    if (!generatedTeams) return;

    const teamAName = bibsTeam === 'A' ? 'TEAM A (bibs)' : 'TEAM A';
    const teamBName = bibsTeam === 'B' ? 'TEAM B (bibs)' : 'TEAM B';

    const text = `⚽ TEAM SELECTIONS

🔵 ${teamAName}:
${generatedTeams.team_a.map((p) => `• ${p.name}`).join('\n')}

🔴 ${teamBName}:
${generatedTeams.team_b.map((p) => `• ${p.name}`).join('\n')}`;

    try {
      // Check if Web Share API is available (mobile devices)
      if (navigator.share) {
        await navigator.share({
          text: text,
          title: 'Team Selections',
        });
      } else {
        // Fallback to WhatsApp Web for desktop
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(whatsappUrl, '_blank');
      }
    } catch (err) {
      // User cancelled share or share failed
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
        alert('Failed to share teams. Please try copying instead.');
      }
    }
  };

  const balanceInfo = generatedTeams ? getBalanceQuality(generatedTeams.balance.rating_difference) : null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Generate Teams</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Select players and generate balanced teams automatically
          </p>
        </div>

        {/* Model Selection - Desktop Tabs */}
        <div className="border-b border-gray-200 hidden md:block">
          <nav className="-mb-px flex space-x-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            {algorithmVisibility.show_algorithm_v1 && (
              <button
                onClick={() => setActiveModel('v1')}
                className={`${
                  activeModel === 'v1'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                M1: Position
              </button>
            )}
            {algorithmVisibility.show_algorithm_v2 && (
              <button
                onClick={() => setActiveModel('v2')}
                className={`${
                  activeModel === 'v2'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                M2: Attributes
                <span className="ml-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-1.5 py-0.5 rounded-full">
                  Exp
                </span>
              </button>
            )}
            {algorithmVisibility.show_algorithm_v3 && (
              <button
                onClick={() => setActiveModel('v3')}
                className={`${
                  activeModel === 'v3'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                M3: Learning
              </button>
            )}
            {algorithmVisibility.show_algorithm_super && (
              <button
                onClick={() => setActiveModel('super')}
                className={`${
                  activeModel === 'super'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Super
                <span className="ml-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 px-1.5 py-0.5 rounded-full">
                  ⭐
                </span>
              </button>
            )}
            {!loadingBeta && betaModeEnabled && algorithmVisibility.show_algorithm_v4 && (
              <button
                onClick={() => setActiveModel('v4-beta')}
                className={`${
                  activeModel === 'v4-beta'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
              >
                M4: Slider
                <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 px-1.5 py-0.5 rounded-full font-semibold">
                  β
                </span>
              </button>
            )}
            {!loadingBeta && betaModeEnabled && algorithmVisibility.show_algorithm_super_beta && (
              <button
                onClick={() => setActiveModel('super-beta')}
                className={`${
                  activeModel === 'super-beta'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
              >
                Super β
              </button>
            )}
            {algorithmVisibility.show_algorithm_v5 && (
              <button
                onClick={() => setActiveModel('v5')}
                className={`${
                  activeModel === 'v5'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
              >
                M5: Dynamic
                <span className="text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-1.5 py-0.5 rounded-full font-semibold">
                  ✨
                </span>
              </button>
            )}
            {algorithmVisibility.show_algorithm_draft && (
              <button
                onClick={() => setActiveModel('draft')}
                className={`${
                  activeModel === 'draft'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
              >
                Draft Mode
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-1.5 py-0.5 rounded-full font-semibold">
                  ⚡ NEW
                </span>
              </button>
            )}
            {algorithmVisibility.show_algorithm_v6 && (
              <button
                onClick={() => setActiveModel('v6')}
                className={`${
                  activeModel === 'v6'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
              >
                V6: Contribution
                <span className="text-xs bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-1.5 py-0.5 rounded-full font-semibold">
                  🧠 AI
                </span>
              </button>
            )}
          </nav>
        </div>

        {/* Model Selection - Mobile Dropdown */}
        <div className="md:hidden">
          <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Algorithm Model
          </label>
          <select
            id="model-select"
            value={activeModel}
            onChange={(e) => setActiveModel(e.target.value as any)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
          >
            <option value="v6">V6: Contribution + Chemistry (AI Learning)</option>
            <option value="v1">Model 1: Position Ratings</option>
            <option value="v2">Model 2: Raw Attributes (Experimental)</option>
            <option value="v3">Model 3: Outcome Learning (New)</option>
            <option value="super">Super Algorithm (Ultimate)</option>
            {!loadingBeta && betaModeEnabled && (
              <>
                <option value="v4-beta">Model 4: Position Slider (BETA)</option>
                <option value="super-beta">Super BETA (Ultimate)</option>
              </>
            )}
            <option value="v5">Model 5: Dynamic Equilibrium (✨ New)</option>
            <option value="draft">Draft Mode (⚡ NEW)</option>
          </select>
        </div>

        {/* Model Description */}
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {activeModel === 'v1' ? (
              <>
                <strong>Position Ratings Model:</strong> Balances teams using calculated position ratings (FWD/MID/DEF/GK).
                This is the default and recommended method.
              </>
            ) : activeModel === 'v2' ? (
              <>
                <strong>Raw Attributes Model (Experimental):</strong> Balances teams by equalizing raw attribute totals
                across Technical, Physical, Mental, and Goalkeeping categories. This experimental approach may produce
                different results compared to Model 1.
              </>
            ) : activeModel === 'v3' ? (
              <>
                <strong>Outcome Learning Model (New):</strong> Uses AI-powered chemistry tracking to learn from match outcomes.
                Balances teams using position ratings (50%), variety tracking (30%), and player chemistry (20%).
                Chemistry improves when players win together and adapts over time. Requires at least 3 matches with scores for optimal results.
              </>
            ) : activeModel === 'v4-beta' ? (
              <>
                <strong>Position Slider Model (BETA):</strong> Uses continuous 0-100 position preferences instead of discrete FWD/MID/DEF positions.
                Balances teams using a hybrid algorithm: 70% contribution balance (weighted ratings based on slider position) + 30% position spread balance.
                Players without position preferences will default to Central Mid (50). Set preferences in the BETA Positions tab for best results.
              </>
            ) : activeModel === 'super-beta' ? (
              <>
                <strong>Super BETA (Ultimate + Position Slider):</strong> Combines the comprehensive Super Algorithm with position slider preferences.
                Uses Position Balance (20%) + Attribute Balance (20%) + Chemistry (25%) + Variety (25%) + Position Quality (10%).
                Position balance uses weighted contributions from slider preferences instead of discrete positions, providing the ultimate balancing experience.
              </>
            ) : activeModel === 'v5' ? (
              <>
                <strong>✨ Dynamic Equilibrium (Model 5):</strong> Advanced tactical team balancing with role-based construction and narrative generation.
                Analyzes players through 8 tactical roles (Playmaker, Finisher, Workhorse, Defender, Creator, Anchor, Sweeper, Goalkeeper),
                distributes weaknesses fairly, identifies multiple win conditions for each team, and generates a tactical narrative explaining the matchup.
                Uses Role Balance (25%) + Weakness Distribution (25%) + Competitive Tension (30%) + Variety (20%) for story-driven team generation.
              </>
            ) : activeModel === 'draft' ? (
              <>
                <strong>⚡ Draft Mode (NEW):</strong> AI-powered snake draft using ABBA pattern for fair team selection.
                First 4 picks: A-B-B-A, then alternating A-B-A-B... Captains are automatically selected based on rating (40%), leadership (30%), and experience (30%).
                Smart AI picks consider position balance (40%), player quality (30%), chemistry (20%), and variety (10%).
                Watch the transparent reasoning behind each pick!
              </>
            ) : (activeModel as string) === 'v6' ? (
              <>
                <strong>🧠 Contribution + Chemistry (V6):</strong> Position-agnostic AI learning system that adapts to your league.
                Calculates each player's contribution score (1-10) from 6 categories: Mobility, Ball Skill, Intelligence, Attacking, Defending, Teamplay.
                Balances teams using Contribution (40%) + Chemistry (40%) + Variety (20%). Auto-detects goalkeeper specialists (GK ≥7.0, outfield &lt;6.0).
                After 10+ matches, the algorithm learns optimal category weights from your match outcomes for personalized team balancing.
              </>
            ) : (
              <>
                <strong>Super Algorithm (Ultimate):</strong> Combines all three models into one comprehensive algorithm.
                Uses Position Balance (20%) + Attribute Balance (20%) + Chemistry (25%) + Variety (25%) + Position Quality (10%).
                This multi-dimensional approach takes the best ideas from Models 1, 2, and 3 for the most balanced teams possible.
              </>
            )}
          </div>
        </Card>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Configuration */}
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1 sm:flex-initial">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Team Size
                  </label>
                  <select
                    value={teamSize}
                    onChange={(e) => setTeamSize(parseInt(e.target.value) as TeamSize)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={5}>5v5 (10 players)</option>
                    <option value={6}>6v6 (12 players)</option>
                    <option value={7}>7v7 (14 players)</option>
                    <option value={8}>8v8 (16 players)</option>
                  </select>
                </div>

                <div className="flex-1 sm:flex-initial">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Match Date
                  </label>
                  <input
                    type="date"
                    value={matchDate}
                    onChange={(e) => setMatchDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between md:justify-end gap-2">
                <div className="text-sm text-gray-600 dark:text-gray-400 sm:text-right">
                  {selectedPlayers.length} of {requiredPlayers} players selected
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    selectedPlayerIds.size === players.length
                      ? deselectAllPlayers()
                      : selectAllPlayers()
                  }
                  className="w-full sm:w-auto"
                >
                  {selectedPlayerIds.size === players.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </div>

            {selectedPlayers.length > 0 && selectedPlayers.length !== requiredPlayers && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded text-sm">
                Need exactly {requiredPlayers} players for {teamSize}v{teamSize}.
                Currently selected: {selectedPlayers.length}
              </div>
            )}

            {selectedPlayers.some((p) => p.calculated === null) && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded text-sm">
                Some selected players don't have complete ratings. Please rate all players first.
              </div>
            )}
          </div>
        </Card>


        {/* Dedicated Goalkeepers */}
        <Card>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Dedicated Goalkeepers (Optional)
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select a dedicated goalkeeper for either team. If none selected, players will rotate in goal.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Team A GK Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team A Goalkeeper
                </label>
                <select
                  value={dedicatedGKTeamA || ''}
                  onChange={(e) => setDedicatedGKTeamA(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">No dedicated GK (Rotation)</option>
                  {selectedPlayers
                    .filter(p => p.calculated?.gk_rating && p.calculated.gk_rating > 0)
                    .map(player => (
                      <option key={player.id} value={player.id}>
                        {player.name} (GK: {player.calculated?.gk_rating?.toFixed(1) || 'N/A'})
                      </option>
                    ))}
                </select>
                {dedicatedGKTeamA && (
                  <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                    ✓ {selectedPlayers.find(p => p.id === dedicatedGKTeamA)?.name} will play in goal for Team A
                  </p>
                )}
              </div>

              {/* Team B GK Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team B Goalkeeper
                </label>
                <select
                  value={dedicatedGKTeamB || ''}
                  onChange={(e) => setDedicatedGKTeamB(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">No dedicated GK (Rotation)</option>
                  {selectedPlayers
                    .filter(p => p.calculated?.gk_rating && p.calculated.gk_rating > 0)
                    .map(player => (
                      <option key={player.id} value={player.id}>
                        {player.name} (GK: {player.calculated?.gk_rating?.toFixed(1) || 'N/A'})
                      </option>
                    ))}
                </select>
                {dedicatedGKTeamB && (
                  <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                    ✓ {selectedPlayers.find(p => p.id === dedicatedGKTeamB)?.name} will play in goal for Team B
                  </p>
                )}
              </div>
            </div>

            {dedicatedGKTeamA && dedicatedGKTeamB && dedicatedGKTeamA === dedicatedGKTeamB && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">
                  ⚠️ Same player cannot be goalkeeper for both teams
                </p>
              </div>
            )}
          </div>
        </Card>
        {/* Player Selection */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">Select Players</h2>
          {players.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No players available. Add some players first!
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedPlayers.map((player) => (
                <label
                  key={player.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedPlayerIds.has(player.id)
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500 dark:border-primary-400'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPlayerIds.has(player.id)}
                    onChange={() => togglePlayerSelection(player.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{player.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {player.calculated && player.calculated.overall_rating != null ? (
                        <>
                          Overall: {player.calculated.overall_rating.toFixed(1)} •{' '}
                          {player.calculated.best_position}
                        </>
                      ) : (
                        <span className="text-yellow-600">Not rated</span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </Card>

        {/* Generate Button */}
        <div className="flex justify-center">
          <Button
            onClick={() => handleGenerate()}
            loading={generating}
            disabled={!canGenerate}
            size="lg"
            className="w-full sm:w-auto px-8"
          >
            {generating ? 'Generating Teams...' : 'Generate Balanced Teams'}
          </Button>
        </div>

        {/* Results */}
        {/* V5 Results Display */}
        {activeModel === 'v5' && v5Result && (
          <div className="space-y-6">
            <div className="border-t-4 border-purple-600 pt-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                ✨ Dynamic Equilibrium Results
              </h2>
              <V5ResultDisplay result={v5Result} />
              <div className="mt-6 flex gap-2">
                <Button variant="secondary" onClick={() => handleGenerate(false)}>
                  Regenerate
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Draft Mode Results */}
        {activeModel === 'draft' && draftResult && (
          <div className="space-y-6">
            <div className="border-t-4 border-green-600 pt-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                ⚡ Draft Mode Results
              </h2>

              {/* Captain Selections */}
              <Card className="mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">👑 Captain Selections</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-2xl">👑</div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Team A Captain</div>
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{draftResult.captains.teamA.player.name}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {draftResult.captains.teamA.reasoning}
                    </div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4 border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-2xl">👑</div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Team B Captain</div>
                        <div className="text-lg font-bold text-red-600 dark:text-red-400">{draftResult.captains.teamB.player.name}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {draftResult.captains.teamB.reasoning}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Draft Picks */}
              <Card className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">📋 Draft Order</h3>
                <div className="space-y-2">
                  {draftResult.picks.map((pick, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        pick.team === 'A'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className={`text-lg font-bold ${pick.team === 'A' ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'} min-w-[40px]`}>
                        #{pick.pickNumber}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">{pick.player.name}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{pick.reasoning}</div>
                      </div>
                      <div className={`text-sm font-medium px-3 py-1 rounded ${
                        pick.team === 'A'
                          ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300'
                          : 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300'
                      }`}>
                        Team {pick.team}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Final Teams */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-3">Team A</h3>
                  <div className="space-y-2">
                    {draftResult.team_a.map((player:any, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded">
                        <span className="font-medium">{player.name}</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{player.best_position || 'MID'} - {player.overall_rating?.toFixed(1) || '0.0'}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Avg Rating: <span className="font-bold text-blue-600 dark:text-blue-400">{draftResult.statistics.team_a.avg_rating.toFixed(2)}</span></div>
                  </div>
                </Card>

                <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-3">Team B</h3>
                  <div className="space-y-2">
                    {draftResult.team_b.map((player:any, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded">
                        <span className="font-medium">{player.name}</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{player.best_position || 'MID'} - {player.overall_rating?.toFixed(1) || '0.0'}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Avg Rating: <span className="font-bold text-red-600 dark:text-red-400">{draftResult.statistics.team_b.avg_rating.toFixed(2)}</span></div>
                  </div>
                </Card>
              </div>

              {/* Balance Info */}
              <Card className="bg-gray-50 dark:bg-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Team Balance</h3>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Quality: <span className="font-bold">{draftResult.balance.quality}</span> |
                  Rating Difference: <span className="font-bold">{draftResult.balance.rating_difference.toFixed(2)}</span>
                </div>
              </Card>

              <div className="mt-6 flex gap-2">
                <Button variant="secondary" onClick={() => handleGenerate(false)}>
                  Regenerate Draft
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* V6 Results */}
        {activeModel === 'v6' && v6Result && (
          <div className="space-y-6">
            <div className="border-t-4 border-cyan-600 pt-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                🧠 V6: Contribution + Chemistry
              </h2>
              <V6ResultDisplay
                result={v6Result}
                bibsTeam={bibsTeam}
                onCopyTeams={handleCopyTeams}
                onSaveMatch={handleSaveMatch}
              />
              <div className="mt-6 flex gap-2">
                <Button variant="secondary" onClick={() => handleGenerate(false)}>
                  Regenerate
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Standard Model Results */}
        {activeModel !== 'v5' && activeModel !== 'draft' && activeModel !== 'v6' && generatedTeams && (
          <div className="space-y-6">
            <div className="border-t-4 border-primary-600 pt-6">
              <div className="space-y-4 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Generated Teams</h2>
                  {balanceInfo && (
                    <div className={`text-sm font-medium ${balanceInfo.color}`}>
                      {balanceInfo.emoji} {balanceInfo.quality} Balance
                      <span className="text-gray-600 dark:text-gray-400 ml-1">
                        ({generatedTeams.balance.rating_difference.toFixed(1)} diff)
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={handleSaveMatch} className="flex-1 sm:flex-initial">
                    Save Match
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => handleGenerate(true)} className="flex-1 sm:flex-initial">
                    Regenerate
                  </Button>
                </div>

                {generatedTeams.is_same_as_previous && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                    <p className="text-amber-800 dark:text-amber-200 text-sm">
                      ⚠️ No better team configuration found. These are the optimal teams for the selected players.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    value={bibsTeam}
                    onChange={(e) => setBibsTeam(e.target.value as 'A' | 'B')}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="A">Team A wears bibs</option>
                    <option value="B">Team B wears bibs</option>
                  </select>
                  <Button size="sm" onClick={() => setShowVideoModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white border-purple-600 dark:bg-purple-500 dark:hover:bg-purple-600">
                    🎬 Create Video
                  </Button>
                  <Button size="sm" onClick={handleShareTeams} className="bg-green-600 hover:bg-green-700 text-white border-green-600 dark:bg-green-500 dark:hover:bg-green-600">
                    📱 Share
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleCopyTeams}>
                    Copy Teams
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Team A */}
                <TeamCard
                  teamName="Team A"
                  color="blue"
                  players={generatedTeams.team_a}
                  stats={generatedTeams.statistics.team_a}
                  attributeBreakdown={generatedTeams.attribute_breakdown?.team_a}
                  model={activeModel}
                />

                {/* Team B */}
                <TeamCard
                  teamName="Team B"
                  color="red"
                  players={generatedTeams.team_b}
                  stats={generatedTeams.statistics.team_b}
                  attributeBreakdown={generatedTeams.attribute_breakdown?.team_b}
                  model={activeModel}
                />
              </div>

              {/* Super Algorithm Analysis */}
              {activeModel === 'super' && (generatedTeams as any).analysis && (
                <Card className="mt-6 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    ⚡ Super Algorithm Analysis
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Multi-dimensional team balancing combining Position Balance, Attribute Balance, Chemistry, Variety, and Position Quality.
                  </p>

                  {/* 5 Scoring Dimensions */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Position Balance</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.position_balance_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.position_balance * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Attribute Balance</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.attribute_balance_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.attribute_balance * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Chemistry</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.chemistry_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.chemistry * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Variety</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.variety_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.variety * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Position Quality</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.position_quality_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.position_quality * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>

                  {/* Total Score */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-2 border-purple-300 dark:border-purple-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Combined Score</div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                          Weighted average of all 5 dimensions
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.total_score.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Team Chemistry Scores */}
                  {((generatedTeams as any).analysis.team_chemistry_a > 0 || (generatedTeams as any).analysis.team_chemistry_b > 0) && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Team A Chemistry</div>
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {(generatedTeams as any).analysis.team_chemistry_a.toFixed(1)}
                        </div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Team B Chemistry</div>
                        <div className="text-xl font-bold text-red-600 dark:text-red-400">
                          {(generatedTeams as any).analysis.team_chemistry_b.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Attribute Breakdown Comparison */}
                  {(generatedTeams as any).analysis.attribute_breakdown_a && (
                    <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-800">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Attribute Breakdown (Model 2)</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                          <div className="text-xs font-medium text-blue-900 dark:text-blue-400 mb-2">Team A</div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">⚡ Technical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.technical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.physical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.mental.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧤 Goalkeeping:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.goalkeeping.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
                          <div className="text-xs font-medium text-red-900 dark:text-red-400 mb-2">Team B</div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">⚡ Technical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.technical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.physical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.mental.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧤 Goalkeeping:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.goalkeeping.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* Super BETA Algorithm Analysis */}
              {activeModel === 'super-beta' && (generatedTeams as any).analysis && (
                <Card className="mt-6 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    ⚡ Super BETA Analysis
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Ultimate team balancing combining Super Algorithm's multi-dimensional approach with position slider preferences.
                  </p>

                  {/* 5 Scoring Dimensions */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Position Balance</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.position_balance_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.position_balance * 100).toFixed(0)}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Using slider weights
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Attribute Balance</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.attribute_balance_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.attribute_balance * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Chemistry</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.chemistry_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.chemistry * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Variety</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.variety_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.variety * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Position Quality</div>
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.position_quality_score.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Weight: {((generatedTeams as any).analysis.weights.position_quality * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>

                  {/* Total Score */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-2 border-purple-300 dark:border-purple-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Combined Score</div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                          Weighted average of all 5 dimensions
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                        {(generatedTeams as any).analysis.total_score.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Team Chemistry Scores */}
                  {((generatedTeams as any).analysis.team_chemistry_a > 0 || (generatedTeams as any).analysis.team_chemistry_b > 0) && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Team A Chemistry</div>
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {(generatedTeams as any).analysis.team_chemistry_a.toFixed(1)}
                        </div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Team B Chemistry</div>
                        <div className="text-xl font-bold text-red-600 dark:text-red-400">
                          {(generatedTeams as any).analysis.team_chemistry_b.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Variety Details */}
                  {(generatedTeams as any).analysis.variety_details && (
                    <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-800">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Variety Analysis</div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <div className="text-gray-600 dark:text-gray-400 mb-1">Recent Matches</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                              {(generatedTeams as any).analysis.variety_details.recentMatchCount}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-600 dark:text-gray-400 mb-1">Repeated Pairings</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                              {(generatedTeams as any).analysis.variety_details.repeatedPairings} / {(generatedTeams as any).analysis.variety_details.totalPairings}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-600 dark:text-gray-400 mb-1">Variety Score</div>
                            <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                              {(generatedTeams as any).analysis.variety_details.score ? (generatedTeams as any).analysis.variety_details.score.toFixed(1) : '100.0'}%
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                          Fewer repeated pairings from recent matches = higher variety score
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contribution Breakdown */}
                  <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-800">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Team Contributions (Position Weighted)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Team A Total</div>
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {(generatedTeams as any).analysis.contribution_a.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Avg: {((generatedTeams as any).analysis.contribution_a / generatedTeams.team_a.length).toFixed(1)}
                        </div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Team B Total</div>
                        <div className="text-xl font-bold text-red-600 dark:text-red-400">
                          {(generatedTeams as any).analysis.contribution_b.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Avg: {((generatedTeams as any).analysis.contribution_b / generatedTeams.team_b.length).toFixed(1)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Attribute Breakdown Comparison */}
                  {(generatedTeams as any).analysis.attribute_breakdown_a && (
                    <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-800">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Raw Attribute Breakdown</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                          <div className="text-xs font-medium text-blue-900 dark:text-blue-400 mb-2">Team A</div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">⚡ Technical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.technical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.physical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.mental.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧤 Goalkeeping:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_a.goalkeeping.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
                          <div className="text-xs font-medium text-red-900 dark:text-red-400 mb-2">Team B</div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">⚡ Technical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.technical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.physical.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.mental.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">🧤 Goalkeeping:</span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(generatedTeams as any).analysis.attribute_breakdown_b.goalkeeping.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* Goalkeeper Rotation Impact */}
              {generatedTeams.goalkeeping && (
                <Card className="mt-6 bg-blue-50 dark:bg-blue-900/20">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    🧤 Goalkeeper Rotation Impact
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    {generatedTeams.goalkeeping.explanation}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-400 mb-2">Team A</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Avg GK Rating:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{generatedTeams.goalkeeping.team_a.avg_gk.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Effective Rating:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{generatedTeams.goalkeeping.team_a.effective_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-200 dark:border-red-800">
                      <div className="text-sm font-medium text-red-900 dark:text-red-400 mb-2">Team B</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Avg GK Rating:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{generatedTeams.goalkeeping.team_b.avg_gk.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Effective Rating:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{generatedTeams.goalkeeping.team_b.effective_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Team Variety */}
              {generatedTeams.variety && (
                <Card className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      🔄 Team Variety
                    </h3>
                    <div className={`text-sm font-medium ${
                      generatedTeams.variety.score >= 8 ? 'text-green-600' :
                      generatedTeams.variety.score >= 6 ? 'text-yellow-600' :
                      generatedTeams.variety.score >= 4 ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {generatedTeams.variety.score >= 8 ? '🟢' :
                       generatedTeams.variety.score >= 6 ? '🟡' :
                       generatedTeams.variety.score >= 4 ? '🟠' : '🔴'}
                      {' '}{generatedTeams.variety.label} ({generatedTeams.variety.score.toFixed(1)}/10)
                    </div>
                  </div>

                  {generatedTeams.variety.avoided_pairings.length > 0 ? (
                    <>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        These players often played together recently but are now separated:
                      </p>
                      <div className="space-y-2">
                        {generatedTeams.variety.avoided_pairings.map((pairing, idx) => {
                          // Debug logging
                          if (idx === 0) {
                            console.log('Avoided pairing sample:', pairing);
                            console.log('Has percentage?', pairing.percentage !== undefined);
                            console.log('Has times_together?', pairing.times_together !== undefined);
                            console.log('Has old format?', (pairing as any).times_played_together !== undefined);
                          }
                          return (
                            <div key={idx} className="flex items-start text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                              <span className="text-green-600 mr-2">✓</span>
                              <div>
                                <span className="font-medium">{pairing.player1}</span>
                                {' & '}
                                <span className="font-medium">{pairing.player2}</span>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {pairing.percentage !== undefined
                                    ? `Together ${pairing.percentage}% (${pairing.times_together}/${pairing.total_matches} matches)`
                                    : `Played together in ${(pairing as any).times_played_together || (pairing as any).frequency || 0} matches`
                                  }
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      All fresh matchups! No frequent pairings to avoid.
                    </p>
                  )}
                </Card>
              )}

              {/* Chemistry Pairings (Model 3 and Super only) */}
              {(activeModel === 'v3' || activeModel === 'super') && (generatedTeams as any).chemistry_pairings && (() => {
                const allPairings = [
                  ...(generatedTeams as any).chemistry_pairings.team_a.map((p: any) => ({ ...p, team: 'A', paired: true })),
                  ...(generatedTeams as any).chemistry_pairings.team_b.map((p: any) => ({ ...p, team: 'B', paired: true }))
                ];

                // Find good pairings (kept together) and bad pairings (separated)
                const goodPaired = allPairings.filter((p: any) => p.chemistry_score >= 6);
                const poorPaired = allPairings.filter((p: any) => p.chemistry_score < 4);

                return (
                  <Card className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      🧪 Algorithm Chemistry Decisions
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      The algorithm considers player chemistry when building teams. Chemistry scores are based on past match outcomes.
                    </p>

                    {/* Good Chemistry - Paired Together */}
                    {goodPaired.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
                          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
                            Strong Partnerships Kept Together
                          </h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          These players have good chemistry (6.0+) and the algorithm paired them on the same team:
                        </p>
                        <div className="space-y-2">
                          {goodPaired.map((pairing: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded border border-green-200 dark:border-green-800">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                    pairing.team === 'A'
                                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                                  }`}>
                                    Team {pairing.team}
                                  </span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {pairing.player_a} & {pairing.player_b}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {pairing.matches_together} matches • {pairing.wins_together} wins ({pairing.win_rate}%)
                                </div>
                              </div>
                              <div className="ml-4 px-3 py-1 rounded font-bold text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                {pairing.chemistry_score.toFixed(1)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Poor Chemistry - Separated */}
                    {poorPaired.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-orange-600 dark:text-orange-400 text-lg">⚠</span>
                          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
                            Poor Chemistry Pairings
                          </h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          These players have poor chemistry (&lt;4.0) but were still paired together:
                        </p>
                        <div className="space-y-2">
                          {poorPaired.map((pairing: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-orange-50 dark:bg-orange-900/20 p-3 rounded border border-orange-200 dark:border-orange-800">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                    pairing.team === 'A'
                                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                                  }`}>
                                    Team {pairing.team}
                                  </span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {pairing.player_a} & {pairing.player_b}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {pairing.matches_together} matches • {pairing.wins_together} wins ({pairing.win_rate}%)
                                </div>
                              </div>
                              <div className="ml-4 px-3 py-1 rounded font-bold text-sm bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
                                {pairing.chemistry_score.toFixed(1)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Pairings Summary */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">All Team Pairings</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-2">Team A ({(generatedTeams as any).chemistry_pairings.team_a.length} pairings)</div>
                          <div className="space-y-1">
                            {(generatedTeams as any).chemistry_pairings.team_a.map((p: any, i: number) => (
                              <div key={i} className="text-xs text-gray-600 dark:text-gray-400 flex justify-between">
                                <span>{p.player_a} & {p.player_b}</span>
                                <span className="font-medium">{p.chemistry_score.toFixed(1)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-red-600 dark:text-red-400 font-medium mb-2">Team B ({(generatedTeams as any).chemistry_pairings.team_b.length} pairings)</div>
                          <div className="space-y-1">
                            {(generatedTeams as any).chemistry_pairings.team_b.map((p: any, i: number) => (
                              <div key={i} className="text-xs text-gray-600 dark:text-gray-400 flex justify-between">
                                <span>{p.player_a} & {p.player_b}</span>
                                <span className="font-medium">{p.chemistry_score.toFixed(1)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })()}

              {generatedTeams.generation_time_ms && (
                <div className="mt-4 text-xs text-gray-500 text-center">
                  Generated in {generatedTeams.generation_time_ms}ms using{' '}
                  {generatedTeams.metadata?.iterations || 10000} iterations
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Team Reveal Video Modal */}
      {generatedTeams && (
        <TeamRevealModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          teamA={generatedTeams.team_a}
          teamB={generatedTeams.team_b}
          bibsTeam={bibsTeam}
        />
      )}
    </Layout>
  );
}

// Team Card Component
function TeamCard({
  teamName,
  color,
  players,
  stats,
  attributeBreakdown,
  model,
}: {
  teamName: string;
  color: 'blue' | 'red';
  players: any[];
  stats: any;
  attributeBreakdown?: {
    technical: number;
    physical: number;
    mental: number;
    goalkeeping: number;
  };
  model: ModelTab;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-500',
    red: 'bg-red-50 border-red-500',
  };

  const headerColors = {
    blue: 'bg-blue-600',
    red: 'bg-red-600',
  };

  return (
    <Card padding={false} className={`border-2 ${colorClasses[color]}`}>
      <div className={`${headerColors[color]} text-white px-4 py-3`}>
        <h3 className="text-xl font-bold">{teamName}</h3>
      </div>

      <div className="p-4 space-y-4">
        {/* Players */}
        <div className="space-y-2">
          {players.map((player, idx) => (
            <div
              key={idx}
              className="py-2 border-b border-gray-200 last:border-0"
            >
              <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{player.name}</div>
              {model === 'v1' ? (
                <div className="text-xs text-gray-500">
                  {player.assigned_position} • {player.overall_rating.toFixed(1)}
                </div>
              ) : model === 'v4-beta' || model === 'super-beta' ? (
                <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Position: {player.position_preference || 50}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      (player.position_preference || 50) <= 30 ? 'bg-blue-100 text-blue-700' :
                      (player.position_preference || 50) <= 70 ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {(player.position_preference || 50) <= 30 ? 'DEF' :
                       (player.position_preference || 50) <= 70 ? 'MID' : 'FWD'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-2">
                    <span>FWD: {player.position_ratings?.fwd_rating?.toFixed(1) || 'N/A'}</span>
                    <span>MID: {player.position_ratings?.mid_rating?.toFixed(1) || 'N/A'}</span>
                    <span>DEF: {player.position_ratings?.def_rating?.toFixed(1) || 'N/A'}</span>
                    <span>Overall: {player.overall_rating?.toFixed(1) || 'N/A'}</span>
                  </div>
                </div>
              ) : model === 'v3' || model === 'super' ? (
                <div className="text-xs text-gray-700 dark:text-gray-300 grid grid-cols-4 gap-x-2">
                  <span>Overall: {player.overall_rating?.toFixed(1) || 'N/A'}</span>
                  <span>FWD: {player.fwd_rating?.toFixed(1) || 'N/A'}</span>
                  <span>MID: {player.mid_rating?.toFixed(1) || 'N/A'}</span>
                  <span>DEF: {player.def_rating?.toFixed(1) || 'N/A'}</span>
                </div>
              ) : (
                <div className="text-xs text-gray-700 dark:text-gray-300 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <span>Tech: {((player.raw_attributes?.passing || 0) + (player.raw_attributes?.first_touch || 0) + (player.raw_attributes?.dribbling || 0) + (player.raw_attributes?.finishing || 0)).toFixed(0)}</span>
                  <span>Phys: {((player.raw_attributes?.pace || 0) + (player.raw_attributes?.stamina || 0) + (player.raw_attributes?.agility || 0) + (player.raw_attributes?.acceleration || 0) + (player.raw_attributes?.balance_strength || 0)).toFixed(0)}</span>
                  <span>Ment: {((player.raw_attributes?.positioning || 0) + (player.raw_attributes?.decision_making || 0) + (player.raw_attributes?.work_rate || 0) + (player.raw_attributes?.composure || 0) + (player.raw_attributes?.tackling || 0) + (player.raw_attributes?.marking || 0) + (player.raw_attributes?.anticipation || 0) + (player.raw_attributes?.leadership || 0) + (player.raw_attributes?.off_the_ball || 0) + (player.raw_attributes?.teamwork || 0)).toFixed(0)}</span>
                  <span>GK: {(player.raw_attributes?.goalkeeping || 0).toFixed(0)}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          {model === 'v4-beta' || model === 'super-beta' ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Team Contribution:</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.total_rating?.toFixed(1) || stats.avg_contribution?.toFixed(1) || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Avg Contribution:</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.avg_rating?.toFixed(1) || stats.avg_contribution?.toFixed(1) || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Position Spread:</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.position_spread?.toFixed(1) || 'N/A'}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Position Ratings Avg:</div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>⚔️ FWD:</span>
                  <span>{stats.avg_fwd?.toFixed(1) || '0.0'}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>⚙️ MID:</span>
                  <span>{stats.avg_mid?.toFixed(1) || '0.0'}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>🛡️ DEF:</span>
                  <span>{stats.avg_def?.toFixed(1) || '0.0'}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>🧤 GK:</span>
                  <span>{stats.avg_gk?.toFixed(1) || '0.0'}</span>
                </div>
              </div>
            </>
          ) : model === 'v1' || model === 'v3' || model === 'super' ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Total Rating:</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.total_rating.toFixed(1)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Average Rating:</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.avg_rating.toFixed(1)}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Position Breakdown:</div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>⚔️ FWD:</span>
                  <span>{stats.avg_fwd.toFixed(1)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>⚙️ MID:</span>
                  <span>{stats.avg_mid.toFixed(1)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                  <span>🛡️ DEF:</span>
                  <span>{stats.avg_def.toFixed(1)}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Team Attribute Totals</div>
              {attributeBreakdown && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">⚡ Technical:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{attributeBreakdown.technical.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{attributeBreakdown.physical.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{attributeBreakdown.mental.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">🧤 Goalkeeping:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{attributeBreakdown.goalkeeping.toFixed(1)}</span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Per Player Average:</div>
                    <div className="flex justify-between text-xs text-gray-900 dark:text-gray-100">
                      <span>Tech: {(attributeBreakdown.technical / players.length).toFixed(1)}</span>
                      <span>Phys: {(attributeBreakdown.physical / players.length).toFixed(1)}</span>
                      <span>Ment: {(attributeBreakdown.mental / players.length).toFixed(1)}</span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
